import React from 'react';
import { TopBar } from '../../components/layout/TopBar';
import { Header } from '../../components/layout/Header';
import { Navigation } from '../../components/layout/Navigation';
import { MobileStickyBar } from '../../components/layout/MobileStickyBar';
import { Footer } from '../../components/layout/Footer';
import { useScreenInit } from '../../useScreenInit';
import { RotateCcw, AlertTriangle, Sparkles, XCircle, Truck, ShieldCheck, Lock, Phone, Mail, CheckCircle2 } from 'lucide-react';
export function OurPolicies() {
  useScreenInit();
  return <div className="min-h-screen flex flex-col bg-brand-background" data-id="element-1558">
      <TopBar data-id="element-1559" />
      <Header data-id="element-1560" />
      <Navigation data-id="element-1561" />

      <main className="flex-grow" data-id="element-1562">
        {/* Hero */}
        <section className="bg-white border-b border-brand-border py-16 lg:py-24" data-id="element-1563">
          <div className="max-w-7xl mx-auto px-4 text-center" data-id="element-1564">
            <span className="inline-block py-1 px-3 rounded-full bg-brand-goldSoft text-brand-charcoal text-xs font-bold tracking-wide mb-4 uppercase border border-brand-gold/30" data-id="element-1565">
              Customer Service
            </span>
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-serif font-bold text-brand-charcoal mb-6 leading-tight" data-id="element-1566">
              Our Policies
            </h1>
            <p className="text-lg lg:text-xl text-brand-muted max-w-3xl mx-auto leading-relaxed" data-id="element-1567">
              Review our return, shipping, damage, warranty, and privacy
              policies to understand how we handle your orders, claims, and
              information.
            </p>
          </div>
        </section>

        {/* Return Authorization Callout */}
        <section className="py-8 bg-brand-background" data-id="element-1568">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1569">
            <div className="bg-brand-goldWash border border-brand-gold/30 rounded-xl p-6 flex flex-col md:flex-row gap-5 items-start" data-id="element-1570">
              <div className="w-12 h-12 rounded-full bg-white text-brand-maroon flex items-center justify-center shrink-0 shadow-sm" data-id="element-1571">
                <AlertTriangle size={24} data-id="element-1572" />
              </div>
              <div data-id="element-1573">
                <h2 className="text-lg font-bold text-brand-charcoal mb-2" data-id="element-1574">
                  A Return Authorization (RGA#) Is Required for All Returns
                </h2>
                <p className="text-brand-charcoal/80 text-sm leading-relaxed" data-id="element-1575">
                  Before sending anything back, contact our customer service
                  offices at{' '}
                  <a href="tel:240-575-5852" className="font-semibold text-brand-maroon hover:underline" data-id="element-1576">
                    240-575-5852
                  </a>{' '}
                  or{' '}
                  <a href="tel:301-620-7585" className="font-semibold text-brand-maroon hover:underline" data-id="element-1577">
                    301-620-7585
                  </a>
                  , and email{' '}
                  <a href="mailto:customerservice@midatlanticmailboxes.com" className="font-semibold text-brand-maroon hover:underline break-all" data-id="element-1578">
                    customerservice@midatlanticmailboxes.com
                  </a>{' '}
                  to obtain a return authorization number (RGA#). Orders
                  returned or discarded without authorization will NOT be
                  processed for a refund.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Return Policy */}
        <section className="py-12 bg-white border-b border-brand-border" data-id="element-1579">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1580">
            <div className="flex items-center gap-4 mb-6" data-id="element-1581">
              <div className="w-12 h-12 rounded-full bg-brand-goldWash text-brand-maroon flex items-center justify-center" data-id="element-1582">
                <RotateCcw size={24} data-id="element-1583" />
              </div>
              <h2 className="text-2xl lg:text-3xl font-serif font-bold text-brand-charcoal" data-id="element-1584">
                Return Policy
              </h2>
            </div>
            <ul className="space-y-3 text-brand-muted leading-relaxed" data-id="element-1585">
              <li className="flex items-start gap-3" data-id="element-1586">
                <CheckCircle2 size={18} className="text-brand-maroon shrink-0 mt-1" data-id="element-1587" />
                <span data-id="element-1588">
                  Returns must be made within{' '}
                  <strong data-id="element-1589">30 days from receipt</strong> of your order, in new
                  condition and the original packaging.
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-1590">
                <CheckCircle2 size={18} className="text-brand-maroon shrink-0 mt-1" data-id="element-1591" />
                <span data-id="element-1592">
                  Many of our manufacturers may or may not allow a return if the
                  item is not defective.
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-1593">
                <CheckCircle2 size={18} className="text-brand-maroon shrink-0 mt-1" data-id="element-1594" />
                <span data-id="element-1595">
                  If approved, a restocking fee applies and varies by
                  manufacturer, with a{' '}
                  <strong data-id="element-1596">minimum restocking fee of 25% or $25</strong>.
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-1597">
                <CheckCircle2 size={18} className="text-brand-maroon shrink-0 mt-1" data-id="element-1598" />
                <span data-id="element-1599">
                  Return shipping charges are the customer's responsibility, and
                  original shipping charges are non-refundable.
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-1600">
                <CheckCircle2 size={18} className="text-brand-maroon shrink-0 mt-1" data-id="element-1601" />
                <span data-id="element-1602">
                  Contact us at{' '}
                  <a href="tel:800-806-7997" className="text-brand-maroon font-semibold hover:underline" data-id="element-1603">
                    800-806-7997
                  </a>{' '}
                  to determine your return requirements. Products may be
                  returned via UPS Ground, FedEx Ground, or USPS.
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-1604">
                <CheckCircle2 size={18} className="text-brand-maroon shrink-0 mt-1" data-id="element-1605" />
                <span data-id="element-1606">
                  Products that have been installed or customized are not
                  returnable.
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-1607">
                <CheckCircle2 size={18} className="text-brand-maroon shrink-0 mt-1" data-id="element-1608" />
                <span data-id="element-1609">
                  A credit will be issued upon return after inspection, minus
                  the restocking fee and any original shipping fees.
                </span>
              </li>
            </ul>
          </div>
        </section>

        {/* Damage & Shipping Claims */}
        <section className="py-12 bg-brand-background border-b border-brand-border" data-id="element-1610">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1611">
            <div className="flex items-center gap-4 mb-6" data-id="element-1612">
              <div className="w-12 h-12 rounded-full bg-white text-brand-maroon flex items-center justify-center shadow-sm" data-id="element-1613">
                <AlertTriangle size={24} data-id="element-1614" />
              </div>
              <h2 className="text-2xl lg:text-3xl font-serif font-bold text-brand-charcoal" data-id="element-1615">
                Damage & Shipping Claims
              </h2>
            </div>
            <div className="bg-white border border-brand-border rounded-xl p-6 shadow-sm" data-id="element-1616">
              <p className="text-brand-charcoal font-semibold mb-4" data-id="element-1617">
                Please check your order upon delivery. All items must be
                inspected for concealed damage or shortage within{' '}
                <span className="text-brand-maroon" data-id="element-1618">3 days of delivery</span> to
                receive credit.
              </p>
              <ul className="space-y-3 text-brand-muted leading-relaxed text-sm" data-id="element-1619">
                <li className="flex items-start gap-3" data-id="element-1620">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-2 shrink-0" data-id="element-1621"></span>
                  <span data-id="element-1622">
                    Report any damages, manufacturer defects, or shipping errors
                    within <strong data-id="element-1623">3 business days</strong> from receipt by
                    calling{' '}
                    <a href="tel:1-800-806-7997" className="text-brand-maroon font-semibold hover:underline" data-id="element-1624">
                      1-800-806-7997
                    </a>
                    .
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1625">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-2 shrink-0" data-id="element-1626"></span>
                  <span data-id="element-1627">
                    Products damaged in shipping must be left in the box as
                    received with all original packing materials.
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1628">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-2 shrink-0" data-id="element-1629"></span>
                  <span data-id="element-1630">
                    Images of damage may be requested for carrier claims.
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-1631">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-2 shrink-0" data-id="element-1632"></span>
                  <span data-id="element-1633">
                    Do not discard any item — a representative from our shipping
                    company may need to inspect the damaged units. Orders
                    returned or discarded without authorization will NOT be
                    refunded.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Custom Orders */}
        <section className="py-12 bg-white border-b border-brand-border" data-id="element-1634">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1635">
            <div className="flex items-center gap-4 mb-6" data-id="element-1636">
              <div className="w-12 h-12 rounded-full bg-brand-goldWash text-brand-maroon flex items-center justify-center" data-id="element-1637">
                <Sparkles size={24} data-id="element-1638" />
              </div>
              <h2 className="text-2xl lg:text-3xl font-serif font-bold text-brand-charcoal" data-id="element-1639">
                Custom Orders & Exceptions
              </h2>
            </div>
            <div className="space-y-4 text-brand-muted leading-relaxed" data-id="element-1640">
              <p data-id="element-1641">
                Custom-made items or special orders — such as address plaques,
                personalized mailboxes, or any engraved or personalized items —
                are non-refundable or returnable unless they are damaged during
                shipping or defective from the manufacturer.
              </p>
              <p data-id="element-1642">
                If an error occurs in customization due to a manufacturer error
                or an error made by MidAtlantic Mailbox, you have the option of
                either a refund or to have it remade.{' '}
                <strong className="text-brand-charcoal" data-id="element-1643">
                  For custom orders, it is extremely important to double-check
                  your entries.
                </strong>
              </p>
              <p data-id="element-1644">
                America's Finest mailboxes and lanterns, as well as Bacova
                mailboxes, are made to order by hand and are non-returnable. Any
                product that has been assembled or modified is non-returnable.
              </p>
            </div>
          </div>
        </section>

        {/* Cancellation */}
        <section className="py-12 bg-brand-background border-b border-brand-border" data-id="element-1645">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1646">
            <div className="flex items-center gap-4 mb-6" data-id="element-1647">
              <div className="w-12 h-12 rounded-full bg-white text-brand-maroon flex items-center justify-center shadow-sm" data-id="element-1648">
                <XCircle size={24} data-id="element-1649" />
              </div>
              <h2 className="text-2xl lg:text-3xl font-serif font-bold text-brand-charcoal" data-id="element-1650">
                Order Cancellation Policy
              </h2>
            </div>
            <p className="text-brand-muted leading-relaxed" data-id="element-1651">
              Many of our products ship within <strong data-id="element-1652">24–48 hours</strong> of
              processing your order. If you find it necessary to cancel an
              order, please notify us immediately at{' '}
              <a href="mailto:customerservice@midatlanticmailboxes.com" className="text-brand-maroon font-semibold hover:underline break-all" data-id="element-1653">
                customerservice@midatlanticmailboxes.com
              </a>
              . If an order has already shipped, it will be your responsibility
              to pay to ship the item back, and restocking fees may apply.
            </p>
          </div>
        </section>

        {/* Shipping */}
        <section className="py-12 bg-white border-b border-brand-border" data-id="element-1654">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1655">
            <div className="flex items-center gap-4 mb-6" data-id="element-1656">
              <div className="w-12 h-12 rounded-full bg-brand-goldWash text-brand-maroon flex items-center justify-center" data-id="element-1657">
                <Truck size={24} data-id="element-1658" />
              </div>
              <h2 className="text-2xl lg:text-3xl font-serif font-bold text-brand-charcoal" data-id="element-1659">
                Shipping Policy
              </h2>
            </div>
            <div className="space-y-4 text-brand-muted leading-relaxed" data-id="element-1660">
              <p data-id="element-1661">
                Please check your order upon delivery. All items must be
                inspected for concealed damage or shortage within 3 days of
                delivery to receive credit.
              </p>
              <p data-id="element-1662">
                Shipping costs vary for each order and are determined by the
                size and weight of the order and the distance between the two
                ZIP codes. We often arrange to have a product shipped directly
                from the manufacturer, which keeps overhead costs down so we can
                pass those savings on to you.
              </p>
              <p data-id="element-1663">
                If you need your order expedited, please call us to arrange a
                rushed order. Some larger items, such as CBUs or bulk orders,
                will ship via Freight.
              </p>
            </div>
          </div>
        </section>

        {/* Warranty & Privacy */}
        <section className="py-12 bg-brand-background border-b border-brand-border" data-id="element-1664">
          <div className="max-w-5xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1665">
            <div className="bg-white border border-brand-border rounded-xl p-6 shadow-sm" data-id="element-1666">
              <div className="flex items-center gap-3 mb-4" data-id="element-1667">
                <div className="w-11 h-11 rounded-full bg-brand-goldWash text-brand-maroon flex items-center justify-center" data-id="element-1668">
                  <ShieldCheck size={22} data-id="element-1669" />
                </div>
                <h2 className="text-xl font-serif font-bold text-brand-charcoal" data-id="element-1670">
                  Warranty
                </h2>
              </div>
              <p className="text-brand-muted text-sm leading-relaxed" data-id="element-1671">
                One-year product and installation warranty from the date of
                service or purchase, per manufacturer guidelines. Warranties do
                not include damage from acts of God (wind, water, or snow) or
                third-party damage such as vandalism or vehicle damage from any
                moving vehicle, package delivery, snow plow, or paving
                equipment.
              </p>
            </div>
            <div className="bg-white border border-brand-border rounded-xl p-6 shadow-sm" data-id="element-1672">
              <div className="flex items-center gap-3 mb-4" data-id="element-1673">
                <div className="w-11 h-11 rounded-full bg-brand-goldWash text-brand-maroon flex items-center justify-center" data-id="element-1674">
                  <Lock size={22} data-id="element-1675" />
                </div>
                <h2 className="text-xl font-serif font-bold text-brand-charcoal" data-id="element-1676">
                  Privacy Statement
                </h2>
              </div>
              <p className="text-brand-muted text-sm leading-relaxed" data-id="element-1677">
                We respect your privacy. Any and all information collected on
                our website will be kept strictly confidential and will not be
                sold, reused, disclosed, or loaned out.
              </p>
            </div>
          </div>
        </section>

        {/* Customer Service CTA */}
        <section className="py-16 lg:py-20 bg-brand-charcoal text-center" data-id="element-1678">
          <div className="max-w-3xl mx-auto px-4" data-id="element-1679">
            <h2 className="text-3xl font-serif font-bold text-white mb-4" data-id="element-1680">
              Questions About a Policy?
            </h2>
            <p className="text-gray-300 mb-8" data-id="element-1681">
              Our customer service team is here to help with returns, claims,
              and order questions.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1682">
              <a href="tel:301-620-7585" className="bg-brand-gold hover:bg-brand-gold/90 text-brand-charcoal px-8 py-3.5 rounded-md font-bold transition-colors shadow-sm w-full sm:w-auto flex items-center justify-center gap-2" data-id="element-1683">
                <Phone size={18} data-id="element-1684" /> Call 301-620-7585
              </a>
              <a href="mailto:customerservice@midatlanticmailboxes.com" className="bg-transparent border-2 border-white hover:bg-white hover:text-brand-charcoal text-white px-8 py-3.5 rounded-md font-bold transition-colors shadow-sm w-full sm:w-auto flex items-center justify-center gap-2" data-id="element-1685">
                <Mail size={18} data-id="element-1686" /> Email Customer Service
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-1687" />
      <MobileStickyBar data-id="element-1688" />
    </div>;
}